import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import type { Hint, Mode } from "@memorang/types";
import type {
	SessionItem,
	SessionResponse,
} from "@memorang/types/src/session/new-session";
import { create } from "zustand";
import type {
	Answer,
	BlockState,
	ItemState,
	SessionState,
	UpdateItemStatesPayload,
} from "./types";

const useSessionStore = create<SessionState>()((set) => ({
	sessionItems: [],
	currentItemIndex: 0,
	currentProgress: 0,
	id: 0,
	numericId: 0,
	isSummativeTest: false,
	isDiagnostic: false,
	currentBlockIndex: 0,
	numItemsRequested: 0,
	breakTimeElapsed: 0,
	mode: "QUESTION",
	totalTime: 0,
	sessionTime: 0,
	numGoal: 0,
	inReviewMode: false,
	timeElapsed: 0,
	startTime: "",
	blockStates: new Map<number, BlockState>(),
	itemStates: new Map<number, ItemState>(),
	updateBreakTimeElapsed: (breakTimeElapsed: number) => {
		set({ breakTimeElapsed });
	},
	updateCompletedTasks: (itemId: number, completed: boolean) => {
		set((state) => {
			const completedTasks = state.completedTasks || {};
			completedTasks[itemId] = completed;
			return { completedTasks: { ...completedTasks } };
		});
	},
	updateTimeElapsed: (timeElapsed: number) => {
		set({ timeElapsed });
	},
	updateAnswerEventInProgress: (isAnswerEventInProgress: boolean) => {
		set({ isAnswerEventInProgress });
	},
	updateEndingSessionInProgress: (isEndingSessionInProgress: boolean) => {
		set({ isEndingSessionInProgress });
	},
	updateCurrentBlockIndex: (index: number) => {
		set({ currentBlockIndex: index });
	},

	toggleShowMoreItemsAlert: () => {
		set((state) => {
			return {
				showMoreItemsAlert: !state.showMoreItemsAlert,
			};
		});
	},
	toggleShowBreakDialog: (show?: boolean) => {
		set(() => {
			return {
				showBreakDialog: show,
			};
		});
	},
	toggleBreakOngoing: (isBreakOngoing: boolean) => {
		set(() => {
			return {
				isBreakOngoing,
			};
		});
	},
	updateBreak: (breakData?: {
		startTime: string;
		allotedTime: number;
		status: "STARTED" | "EXPIRED";
		totalBreakTimeTakenInSeconds?: number;
	}) => {
		if (breakData) {
			set((state) => {
				return {
					breaks: [...(state.breaks || []), breakData],
				};
			});
		} else {
			set(() => {
				return {
					breaks: [],
				};
			});
		}
	},
	updateSession: ({
		sessionResponse,
		isReverseMode,
		mode,
		isSummativeTest,
		inReviewMode,
		numItemsRequested,
		isRecommendedTask,
		isDiagnostic,
		proethicaTestKind,
	}: {
		sessionResponse: SessionResponse;
		isReverseMode?: boolean;
		mode?: Mode;
		isSummativeTest?: boolean;
		inReviewMode?: boolean;
		numItemsRequested?: number;
		isRecommendedTask?: boolean;
		isDiagnostic?: boolean;
		proethicaTestKind?: ProethicaTestKind;
	}) => {
		const initBlockStates = isSummativeTest
			? sessionResponse.sessionItems.reduce((acc, item) => {
					acc.set(item.id!, {
						currentProgress: 0,
						completed: false,
						completedStatuses: {},
						markedItemsForReview: [],
					});
					return acc;
				}, new Map<number, BlockState>())
			: new Map<number, BlockState>();

		const numGoal = sessionResponse.sessionItems.length;

		// const showMoreItemsAlert =
		// 	(numItemsRequested || 0) < numGoal && !isMockedData;

		set({
			sessionItems: sessionResponse.sessionItems,
			numGoal,
			id: sessionResponse.id,
			collectionId: sessionResponse.collectionId,
			isReverseMode,
			mode,
			numericId: sessionResponse.numericId,
			isSummativeTest,
			blockStates: initBlockStates,
			inReviewMode: inReviewMode,
			// showMoreItemsAlert,
			numItemsRequested: numItemsRequested || 0,
			isRecommendedTask,
			isEndingSessionInProgress: true,
			isDiagnostic,
			proethicaTestKind,
		});
	},
	updateCurrentItemIndex: (index: number) => set({ currentItemIndex: index }),
	updateAnswers: (id: number, answer: Answer) => {
		set((state) => {
			const answers = state.answers || new Map<number, Answer>();
			const selectedChoiceIds =
				state.itemStates?.get(id)?.selectedChoiceIds || [];
			answers.set(id, {
				...answer,
				selectedChoiceIds: selectedChoiceIds,
			});
			return {
				answers: new Map(answers),
			};
		});
	},

	updateProgress: (progress: number) => {
		set((state) => {
			const { currentBlockIndex, isSummativeTest } = state;
			const blockId = state.sessionItems?.[currentBlockIndex].id;
			const existingBlockState = state.blockStates.get(blockId!);
			if (isSummativeTest) {
				const newBlockStates = new Map(state.blockStates);
				newBlockStates.set(blockId!, {
					...(existingBlockState || {}),
					currentProgress: progress,
				});
				return { blockStates: newBlockStates };
			}
			return { currentProgress: progress };
		});
	},
	updateProgressForEachBlock: (progress: number, blockId: number) => {
		set((state) => {
			const blockStates = state.blockStates || new Map<number, BlockState>();
			const blockState = blockStates.get(blockId) || {};
			blockStates.set(blockId, { ...blockState, currentProgress: progress });
			return { blockStates: new Map(blockStates) };
		});
	},
	updateHighlightedItems: (itemId: number, highlightedHtml?: string) =>
		set((state) => {
			const { currentBlockIndex } = state;
			const blockId = state.sessionItems?.[currentBlockIndex].id;
			const existingBlockState = state.blockStates.get(blockId!);
			const currentHighlightedItems =
				existingBlockState?.highlightedItems || new Map<number, string>();

			const updatedHighlightedItems = new Map(currentHighlightedItems);
			updatedHighlightedItems.set(itemId, highlightedHtml);

			const updatedBlockState = {
				...existingBlockState,
				highlightedItems: updatedHighlightedItems,
			};

			const updatedBlockStates = new Map(state.blockStates);
			updatedBlockStates.set(blockId!, updatedBlockState);

			return {
				blockStates: updatedBlockStates,
			};
		}),
	updateItemStates: ({
		itemId,
		selectedChoiceId,
		multiSelect,
		correctAnswerIds,
		crossOut,
		scores,
		answered,
		singleReplace,
		currentOrders,
	}: UpdateItemStatesPayload) => {
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		set((state) => {
			const itemStates = state.itemStates || new Map<number, ItemState>();
			const itemState = itemStates.get(itemId);
			let selectedChoiceIds = itemState?.selectedChoiceIds || [];
			const crossedOutChoiceIds = itemState?.crossedOutChoiceIds || [];
			const hints = itemState?.hints;
			const nextCurrentOrders =
				currentOrders !== undefined ? currentOrders : itemState?.currentOrders;
			const index = selectedChoiceIds.indexOf(selectedChoiceId);
			const crossOutIndex = crossedOutChoiceIds.indexOf(selectedChoiceId);

			if (crossOut) {
				if (crossOutIndex > -1) {
					crossedOutChoiceIds.splice(crossOutIndex, 1);
				} else {
					crossedOutChoiceIds.push(selectedChoiceId);
					if (index > -1) {
						selectedChoiceIds.splice(index, 1);
					}
				}
			} else {
				if (singleReplace) {
					selectedChoiceIds = [selectedChoiceId];
				} else if (multiSelect) {
					if (index > -1) {
						selectedChoiceIds.splice(index, 1);
					} else {
						selectedChoiceIds.push(selectedChoiceId);
					}
				} else if (!(correctAnswerIds?.length || answered)) {
					if (index > -1) {
						selectedChoiceIds = [];
					} else {
						selectedChoiceIds = [selectedChoiceId];
					}
				}
				if (crossOutIndex > -1) {
					crossedOutChoiceIds.splice(crossOutIndex, 1);
				}
			}

			itemStates.set(itemId, {
				selectedChoiceIds: [...selectedChoiceIds],
				correctAnswerIds,
				crossedOutChoiceIds: [...crossedOutChoiceIds],
				scores: scores || [],
				answered: answered,
				hints,
				currentOrders: nextCurrentOrders,
			});
			return { itemStates: new Map(itemStates) };
		});
	},
	updateHintsForItem: (itemId: number, hints: Hint[]) =>
		set((state) => {
			const itemStates = state.itemStates || new Map();
			const itemState = itemStates.get(itemId);
			const newItemState = {
				...itemState,
				scores: itemState?.scores || [],
				hints,
			};
			itemStates.set(itemId, newItemState);
			return {
				itemStates: new Map(itemStates),
			};
		}),
	setPreviewChoiceId: (itemId: number, choiceId: string) =>
		set((state) => {
			const itemStates = state.itemStates || new Map();
			const itemState = itemStates.get(itemId);
			itemStates.set(itemId, {
				...itemState,
				scores: itemState?.scores || [],
				previewChoiceId: choiceId,
			});
			return {
				itemStates: new Map(itemStates),
			};
		}),
	// Summative tests never grade per-item, so itemStates lack `answered`/
	// `correctAnswerIds` - without them review mode renders selections as plain
	// (ungraded) and hides the correct-answer reveal. Before entering review we
	// derive the correct ids from each item's choices and flag them answered.
	gradeItemsForReview: (sessionItems: SessionItem[]) =>
		set((state) => {
			const itemStates = new Map(
				state.itemStates ?? new Map<number, ItemState>(),
			);
			for (const item of sessionItems) {
				const itemId = item.id;
				if (itemId == null) {
					continue;
				}
				const existing = itemStates.get(itemId);
				itemStates.set(itemId, {
					...existing,
					selectedChoiceIds: existing?.selectedChoiceIds ?? [],
					scores: existing?.scores ?? [],
					correctAnswerIds: (item.content?.choices ?? [])
						.filter((choice) => choice.isCorrect)
						.map((choice) => choice.id),
					answered: true,
				});
			}
			return { itemStates };
		}),
	updateInReview: (inReviewMode: boolean, sessionItems: SessionItem[]) =>
		set((state) => {
			if (inReviewMode) {
				return {
					inReviewMode,
					persistedCurrentProgress: state.currentProgress,
					currentProgress: 0,
					currentItemIndex: 0,
					persistedItems: state.sessionItems,
					sessionItems,
					numGoal: sessionItems.length,
				};
			}
			return {
				inReviewMode,
				currentProgress: state.persistedCurrentProgress,
				items: state.persistedSessionItems,
			};
		}),
	updateMarkedItemsForReview: (itemId: number) =>
		set((state) => {
			const { currentBlockIndex } = state;
			const blockId = state.sessionItems?.[currentBlockIndex].id;
			const existingBlockState = state.blockStates.get(blockId!);
			const currentMarkedItems = existingBlockState?.markedItemsForReview || [];
			const isItemMarked = currentMarkedItems.includes(itemId);

			const updatedMarkedItems = isItemMarked
				? currentMarkedItems.filter((id) => id !== itemId)
				: [...currentMarkedItems, itemId];

			const updatedBlockState = {
				...existingBlockState,
				markedItemsForReview: updatedMarkedItems,
			};

			const updatedBlockStates = new Map(state.blockStates);
			updatedBlockStates.set(blockId!, updatedBlockState);

			return {
				blockStates: updatedBlockStates,
			};
		}),
	resetSessionStore: () =>
		set({
			currentItemIndex: 0,
			currentProgress: 0,
			itemStates: undefined,
			answers: undefined,
			numGoal: 0,
			isSummativeTest: false,
			isReverseMode: false,
			inReviewMode: false,
			timeElapsed: 0,
			currentBlockIndex: 0,
			blockStates: new Map<number, BlockState>(),
			totalTime: 0,
			sessionTime: 0,
			numItemsRequested: 0,
			isEndingSessionInProgress: false,
			isAnswerEventInProgress: false,
			breaks: [],
			breakTimeElapsed: 0,
			isBreakOngoing: false,
			completedTasks: {},
			isDiagnostic: false,
			collectionId: undefined,
			proethicaTestKind: undefined,
		}),
}));

export default useSessionStore;
